import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  DropdownModule,
  DropdownMenuData,
  DropdownMenuItem,
} from "@vg-constellation/angular-17/dropdown";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { IconModule } from "@vg-constellation/angular-17/icon";
import { IconButtonComponent } from "@vg-constellation/angular-17/button";
import { LinkComponent } from "@vg-constellation/angular-17/link";
import { CardComponent } from "@vg-constellation/angular-17/card";
import { IMaskModule } from "angular-imask";

import {
  Beneficiary,
  BeneficiarySectionType,
  DropdownStateChangeEvent,
  getCardDensity,
  getBeneficiaryDisplayName,
} from "@models/beneficiary.model";
import { Person } from "@services/zok";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";
import * as content from "@services/content/guided-flow/account-details-content";

@Component({
  selector: "app-beneficiary-card",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    IconModule,
    IconButtonComponent,
    LinkComponent,
    CardComponent,
    IMaskModule,
    DropdownTypeaheadDirective,
  ],
  templateUrl: "./beneficiary-card.component.html",
  styleUrls: ["./beneficiary-card.component.scss"],
})
export class BeneficiaryCardComponent implements OnInit, OnChanges {
  @Input() beneficiary!: Beneficiary;
  @Input() section: BeneficiarySectionType = "PRIMARY";
  @Input() householdMembers: DropdownMenuItem[] = [];
  @Input() householdPeople: Person[] = [];

  @Output() edit = new EventEmitter<Beneficiary>();
  @Output() remove = new EventEmitter<Beneficiary>();
  @Output() distributionChange = new EventEmitter<{ beneficiary: Beneficiary; value: number }>();
  @Output() personChange = new EventEmitter<{ beneficiary: Beneficiary; memberId: string }>();

  protected readonly content = content;

  /**
   * Custom imask configuration for distribution percentage input.
   * Enforces: integers only, 0-100 range, auto-appended % suffix.
   */
  protected readonly distributionMask = {
    mask: "VALUE%",
    lazy: false,
    blocks: {
      VALUE: {
        mask: Number,
        scale: 0,
        signed: false,
        min: 0,
        max: 100,
        thousandsSeparator: "",
        padFractionalZeros: false,
        radix: ".",
      },
    },
  };

  personDropdownData: DropdownMenuData = { groups: [] };
  activePersonId = "";
  cardDensity: "small" | "medium" = "medium";
  dropdownVisible = true; // Used to force dropdown re-render when person data changes
  distributionValue: string = "";

  get displayName(): string {
    return getBeneficiaryDisplayName(this.beneficiary, this.householdPeople);
  }

  ngOnInit(): void {
    this.buildPersonDropdown();
    this.cardDensity = getCardDensity();
    this.distributionValue = String(this.beneficiary.distributionPercentage);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild dropdown when householdMembers or householdPeople changes (e.g., after editing a person's name)
    if (
      (changes["householdMembers"] && !changes["householdMembers"].firstChange) ||
      (changes["householdPeople"] && !changes["householdPeople"].firstChange)
    ) {
      // Force complete dropdown re-render by toggling visibility
      this.dropdownVisible = false;
      setTimeout(() => {
        this.buildPersonDropdown();
        this.dropdownVisible = true;
      });
    }

    // Sync local value when beneficiary input changes
    if (changes["beneficiary"] && !changes["beneficiary"].firstChange) {
      this.distributionValue = String(this.beneficiary.distributionPercentage);
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  private buildPersonDropdown(): void {
    const currentPersonItem: DropdownMenuItem = {
      labelText: this.displayName,
      id: this.beneficiary.householdMemberId || "current-person",
    };

    const otherMembers = this.householdMembers.filter(
      (m) => m.id !== this.beneficiary.householdMemberId,
    );

    this.personDropdownData = {
      groups: [{ items: [currentPersonItem, ...otherMembers] }],
    };

    this.activePersonId = currentPersonItem.id?.toString() || "";
  }

  handlePersonDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId && selectedId !== this.activePersonId) {
      WebLogger.log({
        message: "BeneficiaryCard person changed",
        payload: {
          event_type: "beneficiary_card_person_changed",
          previous_member_id: this.activePersonId,
          new_member_id: selectedId,
        },
      });
      this.activePersonId = selectedId;
      this.personChange.emit({ beneficiary: this.beneficiary, memberId: selectedId });
    }
  }

  handleEditClick(): void {
    this.edit.emit(this.beneficiary);
  }

  handleRemoveClick(): void {
    this.remove.emit(this.beneficiary);
  }

  /**
   * Handles distribution percentage changes from the imask input.
   * Emits the distributionChange event with the parsed integer value.
   */
  handleDistributionChange(): void {
    const numValue = parseInt(this.distributionValue, 10) || 0;
    this.distributionChange.emit({ beneficiary: this.beneficiary, value: numValue });
  }
}
